import React, { useRef, useState } from 'react';

import {
  addVarietyToProduct,
  createProduct,
  uploadFile,
} from '../api';
import ProductFormDialog from './ProductFormDialog';
import ProductCreationWizard from './productCreation/ProductCreationWizard';
import {
  buildFormatPayload,
  buildProductPayload,
  buildVarietyPayload,
} from './productCreation/productCreationDrafts';
import {
  EMPTY_PRODUCT_CREATION_PROGRESS,
  resumeProductCreation,
} from './productCreation/resumableProductCreation';

const ProductCreationModal = ({
  onClose,
  onProductCreated,
  branchId,
  canCreateProduct = true,
  categories = [],
  categoriesLoading = false,
  categoriesError = '',
  taxes = [],
  taxesLoading = false,
  taxesError = '',
  canReadTaxes = true,
  canUpdateProduct = true,
}) => {
  const [progress, setProgress] = useState(EMPTY_PRODUCT_CREATION_PROGRESS);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const progressRef = useRef(EMPTY_PRODUCT_CREATION_PROGRESS);
  const uploadedUrlsRef = useRef({});
  const savingRef = useRef(false);
  const hasPartialProgress = Boolean(progress?.product);
  const supportsCompleteFlow = canUpdateProduct;

  const updateProgress = (nextProgress) => {
    progressRef.current = nextProgress;
    setProgress(nextProgress);
  };

  const requestClose = () => {
    if (isSaving) return;
    if (hasPartialProgress && typeof window !== 'undefined') {
      const shouldClose = window.confirm(
        'Parte del producto ya se creó. Si cierras, esos datos quedarán guardados y perderás los cambios pendientes. ¿Cerrar de todos modos?'
      );
      if (!shouldClose) return;
    }
    onClose?.(false);
  };

  const uploadDraftImage = async (key, file) => {
    if (!file) return null;
    const cachedUpload = uploadedUrlsRef.current[key];
    if (cachedUpload?.file === file) return cachedUpload.url;
    const uploaded = await uploadFile(file);
    if (!uploaded?.url) throw new Error('No se pudo subir una de las imágenes.');
    uploadedUrlsRef.current[key] = { file, url: uploaded.url };
    return uploaded.url;
  };

  const handleIdentitySubmit = async (values) => {
    if (!canCreateProduct) return;
    if (!branchId) throw new Error('Selecciona una sucursal para crear el producto.');
    const {
      productImageFile,
      removeProductImage: _removeProductImage,
      ...payload
    } = values;
    if (productImageFile) {
      const uploaded = await uploadFile(productImageFile);
      if (!uploaded?.url) throw new Error('No se pudo subir la imagen del producto.');
      payload.productImage = uploaded.url;
    }
    const product = await createProduct(branchId, payload);
    await onProductCreated?.(product);
    onClose?.(false);
  };

  const handleSubmit = async ({ productDraft, varietyDrafts }) => {
    if (savingRef.current) return;
    if (!canCreateProduct) return;
    if (!branchId) throw new Error('Selecciona una sucursal para crear el producto.');

    savingRef.current = true;
    setIsSaving(true);
    setSaveStatus('Creando producto...');
    try {
      const completed = await resumeProductCreation({
        progress: progressRef.current,
        productDraft,
        varietyDrafts,
        createProduct: async (draft) => {
          setSaveStatus('Creando producto...');
          const productImage = await uploadDraftImage('product', draft.imageFile);
          return createProduct(branchId, {
            ...buildProductPayload(draft, categories),
            ...(productImage ? { productImage } : {}),
          });
        },
        createVariety: async (draft, product) => {
          setSaveStatus(`Creando variedad ${draft.varietyName.trim()}...`);
          const varietyImage = await uploadDraftImage(`variety:${draft._draftKey}`, draft.imageFile);
          const formats = [];
          for (const formatDraft of draft.formats || []) {
            const priceImage = await uploadDraftImage(`format:${draft._draftKey}:${formatDraft._draftKey}`, formatDraft.imageFile);
            const { varietyId: _varietyId, ...format } = buildFormatPayload({ formatDraft });
            formats.push({ ...format, ...(priceImage ? { priceImage } : {}) });
          }
          return addVarietyToProduct(branchId, {
            ...buildVarietyPayload(draft, product.id),
            ...(varietyImage ? { varietyImage } : {}),
            formats,
          });
        },
        onProgress: updateProgress,
      });
      updateProgress(completed);
      setSaveStatus('Producto creado correctamente.');
      await onProductCreated?.(completed.product);
      onClose?.(false);
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  if (!supportsCompleteFlow) {
    return (
      <ProductFormDialog
        mode="create"
        categories={categories}
        categoriesLoading={categoriesLoading}
        categoriesError={categoriesError}
        taxes={taxes}
        taxesLoading={taxesLoading}
        taxesError={taxesError}
        canReadTaxes={canReadTaxes}
        canSave={canCreateProduct}
        onClose={requestClose}
        onSubmit={handleIdentitySubmit}
      />
    );
  }

  return (
    <ProductCreationWizard
      branchId={branchId}
      categories={categories}
      categoriesLoading={categoriesLoading}
      categoriesError={categoriesError}
      taxes={taxes}
      taxesLoading={taxesLoading}
      taxesError={taxesError}
      canReadTaxes={canReadTaxes}
      canCreateProduct={canCreateProduct}
      canUpdateProduct={canUpdateProduct}
      isSaving={isSaving}
      hasPartialProgress={hasPartialProgress}
      creationProgress={progress}
      saveStatus={saveStatus}
      onClose={requestClose}
      onSubmit={handleSubmit}
    />
  );
};

export default ProductCreationModal;
